
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle,
  Smartphone, Download, Bell, CloudOff,
  Zap, RefreshCw, Shield, HelpCircle
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";

interface MobileAppContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const MobileAppContent: React.FC<MobileAppContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "mobile-overview") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Mobile App Overview</h1>
          <p className="text-gray-500 mt-2">
            Take the power of the DGK Platform with you on the go with our mobile applications.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 12, 2023</span>
            <Badge variant="outline" className="ml-2">Essential</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="mobile-introduction">Introduction to the Mobile Experience</h2>
          <p>
            The DGK Platform mobile app extends the functionality of our web platform to your mobile device, 
            enabling you to manage your freelance business or find talent on the go. Our app is designed to 
            provide a seamless experience across devices with optimized interfaces for both phones and tablets.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <Download className="h-5 w-5 text-blue-500 mr-2" />
                  <h3 className="font-medium">iOS App</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Available for iPhone and iPad with iOS 12.0 or later.
                </p>
                <Button size="sm" className="w-full">
                  Download on App Store
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <Download className="h-5 w-5 text-green-500 mr-2" />
                  <h3 className="font-medium">Android App</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Compatible with Android 8.0 (Oreo) and later devices.
                </p>
                <Button size="sm" className="w-full">
                  Get it on Google Play
                </Button>
              </CardContent>
            </Card>
          </div>

          <h2 id="key-features">Key Mobile Features</h2>
          <p>
            Our mobile app includes most features available on the web platform, optimized for mobile use:
          </p>
          
          <div className="space-y-4 my-6">
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600">
                  <Bell className="h-5 w-5" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Real-time Notifications</h3>
                <p className="text-gray-600">
                  Receive instant alerts for new messages, project updates, and opportunities matching your profile.
                </p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 text-green-600">
                  <MessageCircle className="h-5 w-5" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Messaging & Communication</h3>
                <p className="text-gray-600">
                  Chat with clients or freelancers, participate in video calls, and share files from your device.
                </p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600">
                  <Smartphone className="h-5 w-5" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Job Swipe</h3>
                <p className="text-gray-600">
                  Our popular job matching feature is optimized for mobile with intuitive swipe gestures.
                </p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 text-amber-600">
                  <CloudOff className="h-5 w-5" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Offline Access</h3>
                <p className="text-gray-600">
                  Access key information and work on drafts even when you don't have an internet connection.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-2">Pro Tips for Mobile Users</h3>
            <ul className="text-sm text-blue-700 space-y-2 pl-6 list-disc">
              <li>Enable biometric authentication for quick and secure access.</li>
              <li>Set up your notification preferences to avoid alert fatigue.</li>
              <li>Use the "Save for Later" feature to bookmark jobs you want to review in detail on your desktop.</li>
              <li>Download important files and proposals for offline access before traveling.</li>
            </ul>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["mobile-overview"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("mobile-overview", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("mobile-overview", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("mobile-overview") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("mobile-overview")}
            >
              {bookmarkedArticles.includes("mobile-overview") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "ios-guide") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">iOS App Guide</h1>
          <p className="text-gray-500 mt-2">
            Detailed guide for using the DGK Platform app on iPhone and iPad devices.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 15, 2023</span>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2>Getting Started with iOS</h2>
          <p>
            The DGK Platform iOS app provides a seamless experience for managing your freelance career or finding talent on Apple devices. This guide covers installation, setup, and key features specific to the iOS version.
          </p>

          <Accordion type="single" collapsible className="w-full my-6">
            <AccordionItem value="installation">
              <AccordionTrigger className="text-base font-medium">
                Installation & Setup
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    Follow these steps to install and set up the DGK Platform app on your iOS device:
                  </p>
                  <ol className="space-y-2 list-decimal pl-5">
                    <li>Open the App Store on your iPhone or iPad</li>
                    <li>Search for "DGK Platform" in the search bar</li>
                    <li>Tap "Get" or the download icon to install the app</li>
                    <li>Once installed, tap "Open" or find the app icon on your home screen</li>
                    <li>Log in with your existing DGK Platform credentials or create a new account</li>
                    <li>When prompted, enable notifications for time-sensitive updates</li>
                    <li>Set up FaceID or TouchID for secure access (recommended)</li>
                  </ol>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="ios-features">
              <AccordionTrigger className="text-base font-medium">
                iOS-Specific Features
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    Our iOS app takes advantage of unique Apple platform capabilities:
                  </p>
                  <ul className="space-y-2 list-disc pl-5">
                    <li><strong>Widgets:</strong> Add DGK Platform widgets to your home screen for quick access to recent messages, upcoming deadlines, and earnings stats</li>
                    <li><strong>Shortcuts:</strong> Create custom Siri shortcuts for common actions like checking new jobs or sending status updates</li>
                    <li><strong>Handoff:</strong> Seamlessly transition from working on your iPhone to your Mac or iPad</li>
                    <li><strong>iCloud Sync:</strong> Your drafts and saved items sync across all Apple devices</li>
                    <li><strong>Apple Pay:</strong> Secure payment processing for marketplace purchases</li>
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="ipad-support">
              <AccordionTrigger className="text-base font-medium">
                iPad Optimization
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    The iPad version offers enhanced features for the larger screen:
                  </p>
                  <ul className="space-y-2 list-disc pl-5">
                    <li><strong>Split View:</strong> Work on proposals while referencing job details side-by-side</li>
                    <li><strong>Apple Pencil Support:</strong> Annotate documents and create sketches for client feedback</li>
                    <li><strong>Enhanced Keyboard Support:</strong> Extensive keyboard shortcuts for power users</li>
                    <li><strong>Multi-tasking:</strong> Slide Over and Split View support for maximum productivity</li>
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <h2>Troubleshooting iOS Issues</h2>
          <p>
            If you encounter problems with the iOS app, try these common solutions:
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 my-6">
            <h3 className="text-base font-medium mb-3">Common iOS Issues</h3>
            <div className="space-y-4">
              <div>
                <p className="font-medium text-sm mb-1">App Crashing on Launch</p>
                <p className="text-sm text-gray-600">
                  Try force-closing the app and restarting. If that doesn't work, reinstall the app. Make sure your iOS is updated to the latest version.
                </p>
              </div>
              <div>
                <p className="font-medium text-sm mb-1">Notifications Not Working</p>
                <p className="text-sm text-gray-600">
                  Check your notification settings in both the app and iOS Settings. Ensure Background App Refresh is enabled for the DGK Platform app.
                </p>
              </div>
              <div>
                <p className="font-medium text-sm mb-1">Sync Issues</p>
                <p className="text-sm text-gray-600">
                  Ensure you're signed in with the same account on all devices. Check your internet connection and try toggling Airplane Mode on and off.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["ios-guide"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("ios-guide", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("ios-guide", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("ios-guide") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("ios-guide")}
            >
              {bookmarkedArticles.includes("ios-guide") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
